import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { MessageSquarePlus } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function FloatingFeedbackButton({ isDarkMode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();

  const [showTooltip, setShowTooltip] = useState(false);
  useEffect(() => {
    const hasSeenTooltip = localStorage.getItem('feedback-tooltip-seen');

    if (!hasSeenTooltip) {
      setShowTooltip(true);

      const timer = setTimeout(() => {
        setShowTooltip(false);
        localStorage.setItem('feedback-tooltip-seen', 'true');
      }, 2200);

      return () => clearTimeout(timer);
    }
  }, []);

  const hiddenRoutes = [
    '/app/feedback',
    '/login',
    '/signup',
    '/forgot-password',
    '/onboarding',
    '/app/payment-success',
    '/app/payment-cancel',
  ];

  if (hiddenRoutes.includes(location.pathname)) return null;

  const handleClick = () => {
    setShowTooltip(false);
    navigate('/app/feedback');
  };

  const buttonClasses = isDarkMode
    ? `
      border border-white/10
      bg-[rgba(15,23,42,0.82)]
      text-slate-100
      shadow-[0_10px_30px_rgba(0,0,0,0.35)]
      hover:bg-[rgba(15,23,42,0.92)]
      hover:border-indigo-400/20
      focus-visible:ring-indigo-400/30
    `
    : `
      border border-slate-200/80
      bg-white/92
      text-slate-700
      shadow-[0_10px_30px_rgba(15,23,42,0.10)]
      hover:bg-white
      hover:border-indigo-200
      focus-visible:ring-indigo-500/20
    `;

  const iconWrapClasses = isDarkMode
    ? `
      bg-indigo-500/12
      text-indigo-300
      ring-1 ring-inset ring-indigo-400/10
      group-hover:bg-indigo-500/16
    `
    : `
      bg-indigo-50
      text-indigo-600
      ring-1 ring-inset ring-indigo-100
      group-hover:bg-indigo-100
    `;

  const glowClasses = isDarkMode ? 'bg-indigo-500/14' : 'bg-indigo-400/14';

  const tooltipClasses = isDarkMode
    ? `
      border border-white/10
      bg-[rgba(15,23,42,0.94)]
      text-slate-100
      shadow-[0_12px_30px_rgba(0,0,0,0.30)]
    `
    : `
      border border-slate-200/80
      bg-white
      text-slate-700
      shadow-[0_12px_30px_rgba(15,23,42,0.12)]
    `;

  return (
    <div className="pointer-events-none fixed bottom-5 right-5  md:bottom-7 md:right-7">
      <div
        className={`absolute inset-0 rounded-full blur-2xl ${glowClasses}`}
        aria-hidden="true"
      />

      <AnimatePresence>
        {showTooltip && (
          <motion.div
            initial={
              prefersReducedMotion ? false : { opacity: 0, y: 8, scale: 0.96 }
            }
            animate={prefersReducedMotion ? {} : { opacity: 1, y: 0, scale: 1 }}
            exit={prefersReducedMotion ? {} : { opacity: 0, y: 6, scale: 0.96 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className={`
              pointer-events-none absolute bottom-[calc(100%+12px)] right-0
              rounded-xl px-3 py-2 text-xs font-medium whitespace-nowrap
              backdrop-blur-xl
              ${tooltipClasses}
            `}
            role="status"
            aria-live="polite"
          >
            Send us your feedback
            <span
              className={`
                absolute -bottom-1 right-5 h-2.5 w-2.5 rotate-45
                border-r border-b
                ${
                  isDarkMode
                    ? 'border-white/10 bg-[rgba(15,23,42,0.94)]'
                    : 'border-slate-200/80 bg-white'
                }
              `}
              aria-hidden="true"
            />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={handleClick}
        aria-label="Open feedback page"
        className={`
          group pointer-events-auto relative inline-flex items-center gap-3
          rounded-full px-3.5 py-3 pr-4
          backdrop-blur-xl
          transition-[background,border-color,color,box-shadow,transform]
          duration-200
          focus-visible:outline-none focus-visible:ring-4
          ${buttonClasses}
        `}
        initial={
          prefersReducedMotion ? false : { opacity: 0, y: 14, scale: 0.96 }
        }
        animate={prefersReducedMotion ? {} : { opacity: 1, y: 0, scale: 1 }}
        whileHover={prefersReducedMotion ? {} : { y: -2 }}
        whileTap={prefersReducedMotion ? {} : { scale: 0.985 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      >
        <span
          className={`
            flex h-10 w-10 items-center justify-center rounded-full
            transition-colors duration-200
            ${iconWrapClasses}
          `}
        >
          <MessageSquarePlus size={18} strokeWidth={2.1} />
        </span>

        <span className="hidden whitespace-nowrap text-sm font-semibold tracking-[-0.01em] sm:inline">
          Feedback
        </span>
      </motion.button>
    </div>
  );
}
